import * as React from "react";
import { useState } from "react";
import { ChevronDown, Link2, RefreshCw, Tags, UserCheck } from "lucide-react";
import { Badge, Collapsible } from "@/components/ui";
import { cn } from "@/lib/utils";

const REASONS = [
  {
    icon: Link2,
    title: "Set it up once",
    text: "Paste the route into an email template or web page once. Every client who gets it uses the same link, and clients you already have are recognized, so you don't get duplicates.",
  },
  {
    icon: UserCheck,
    title: "Credited to the right Player",
    text: "Each response goes to the Player who sent the email, and they're notified. When the form asks for public reviews, it shows that Player's own Google, Zillow or Facebook links.",
  },
  {
    icon: RefreshCw,
    title: "Change the form without editing emails",
    text: 'Choose "Your default form" and the link opens the default for each Player\'s region or branch. Change a default in Manage Defaults and every email uses the new form.',
  },
  {
    icon: Tags,
    title: "See which emails get responses",
    text: "Give the route in each email a campaign name, and every response shows which campaign it came from.",
  },
];

type Need = "Recommended" | "Optional" | "If you have it";

const VALUES: { value: string; need: Need; does: string }[] = [
  {
    value: "Player email",
    need: "Recommended",
    does: "Sends each response to the right player. Without it, responses arrive with no player.",
  },
  {
    value: "Client ID",
    need: "Recommended",
    does: "Your CRM's ID for the client. The most reliable way to recognize them, even when their email changes, and it keeps personal details out of the link.",
  },
  { value: "Client email", need: "Optional", does: "Another way to recognize clients you already have." },
  { value: "Language", need: "Optional", does: "Opens the form in the client's language, when the form has it." },
  { value: "Campaign", need: "Optional", does: "Shows which email or page each response came from." },
  {
    value: "SocialCoach Fan ID",
    need: "If you have it",
    does: "Only if your CRM already stores our Fan IDs. Recognizes the client exactly.",
  },
];

/** Why routes exist, always shown; what the link carries sits behind "What's in the link". */
export function FormRoutesIntro() {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-sm flex flex-col gap-4"
      data-testid="form-routes-intro"
    >
      <div className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold text-slate-800">
          Put any SocialCoach form in your emails with one link
        </h2>
        <p className="text-sm text-slate-500">
          Use it to ask for reviews after closing, check in mid-loan, or collect leads from your website and campaigns.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {REASONS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-primary">
              <Icon className="size-4" />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-slate-800">{title}</span>
              <span className="text-xs text-slate-500 leading-relaxed">{text}</span>
            </span>
          </div>
        ))}
      </div>
      <Collapsible.Content className="flex flex-col gap-2 border-t border-slate-100 pt-4">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">What's in the link</span>
        <p className="text-xs text-slate-500">
          Your CRM or website fills these in for each client. For TotalExpert, the merge fields are already set up
          below.
        </p>
        <table className="w-full text-sm" data-testid="intro-values">
          <tbody className="divide-y divide-slate-100">
            {VALUES.map(row => (
              <tr key={row.value}>
                <td className="py-2 pr-3 font-medium text-slate-700 whitespace-nowrap align-top">{row.value}</td>
                <td className="py-2 pr-3 align-top">
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px] px-1.5 py-0 whitespace-nowrap",
                      row.need === "Recommended" && "border-primary/30 bg-primary/5 text-primary"
                    )}
                  >
                    {row.need}
                  </Badge>
                </td>
                <td className="py-2 text-slate-600">{row.does}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-slate-500">
          The link works even with nothing filled in: the client types their name and email on the form, and the
          response arrives with no player.
        </p>
      </Collapsible.Content>
      <Collapsible.Trigger className="group flex items-center gap-1 self-start text-sm font-semibold text-primary hover:underline cursor-pointer">
        {open ? "Hide details" : "What's in the link"}
        <ChevronDown className="size-4 transition-transform group-data-[panel-open]:rotate-180" />
      </Collapsible.Trigger>
    </Collapsible>
  );
}
